import { Injectable, ElementRef, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Subject, firstValueFrom } from 'rxjs';
import { ApiService } from '@gatho/services';
import { BrandingSettings } from '@shared/models';

@Injectable({
  providedIn: 'root'
})
export class BrandingService {

  public brandingLoaded = false;
  private brandingLoadedSub = new Subject<Boolean>;
  private brandingLoaded$ = this.brandingLoadedSub.asObservable();

  public branding$ = new Subject<BrandingSettings>();

  public cached?: BrandingSettings;

  constructor(
    private apiService: ApiService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.loadFromStorage();
    this.cacheBrandingSettings();
    this.setGlobalBranding();
  }

  async brandingAvailable() {
    if (!this.brandingLoaded) {
      await firstValueFrom(this.brandingLoaded$);
    }
  }

  setBrandingLoaded() {
    this.applyCustomCss();
    this.brandingLoaded = true;
    this.brandingLoadedSub.next(true);
  }

  async cacheBrandingSettings(): Promise<void> {
    const data = await this.apiService.getBrandingSettings();
    if ('uuid' in data) {
      localStorage.setItem('branding', JSON.stringify(data));
      this.cached = data;
    }
    this.setBrandingLoaded();
  }

  loadFromStorage(): void {
    const data = localStorage.getItem('branding');
    if (data) {
      this.cached = JSON.parse(data);
      this.setBrandingLoaded();
    }
  }

  getBrandingSetting(key: string): string | null {
    return this.cached?.[key] ?? null;
  }

  private applyCustomCss(): void {
    const css = this.cached?.customCss;
    let style = this.document.querySelector<HTMLStyleElement>('style#gatho-custom-css');
    if (typeof css !== 'string' || !css.trim()) {
      style?.remove();
      return;
    }
    if (!style) {
      style = this.document.createElement('style');
      style.id = 'gatho-custom-css';
    }
    style.textContent = css;
    this.document.head.appendChild(style);
  }

  setCssProperty(elementRef: ElementRef, name: string, setting: string, custom?: boolean): void {
    // Get value from branding settings cache
    if (!custom) {
      const value = this.getBrandingSetting(setting);
      if (value) {
        elementRef.nativeElement.style.setProperty(name, value);
      }
    }
    // Custom values get used verbatim
    else {
      elementRef.nativeElement.style.setProperty(name, setting);
    }
  }

  async setGlobalBranding() {
    await this.brandingAvailable();
    const faviconEl = document.querySelector('link[rel="icon"]');
    const faviconSetting = this.getBrandingSetting('favicon');
    if (faviconEl && faviconSetting) {
      faviconEl.setAttribute('href', faviconSetting);
    }
    const appIconEl = document.querySelector('link[rel="apple-touch-icon"]');
    const appIconSetting = this.getBrandingSetting('appIcon');
    if (appIconEl && appIconSetting) {
      appIconEl.setAttribute('href', appIconSetting);
    }
    const metaDescriptionEl = document.querySelector('meta[name="description"]');
    const metaDescriptionSetting = this.getBrandingSetting('metaDescription');
    if (metaDescriptionEl && metaDescriptionSetting) {
      metaDescriptionEl.setAttribute('content', metaDescriptionSetting);
    }
  }

}
